import { Injectable, NgZone } from '@angular/core';
import { fromEvent, merge, Subject } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { AuthService } from './auth.service';

/**
 * RefreshTokenValidationService - Validates refresh token on page visibility/focus changes
 * 
 * Purpose:
 * - Detect when user returns from system lock/sleep/hibernate
 * - Detect when user switches back to browser tab
 * - Validate that refresh token hasn't expired during inactivity
 * - Force logout if refresh token is expired
 * 
 * Implementation:
 * - Listens to visibilitychange events
 * - Listens to focus events
 * - Validates refresh token expiration
 * - Triggers logout if expired
 */
@Injectable({
  providedIn: 'root'
})
export class RefreshTokenValidationService {
  
  private visibilityCheckSubject$ = new Subject<string>();
  private isDestroyed = false;

  constructor(
    private authService: AuthService,
    private ngZone: NgZone
  ) {}

  /**
   * Start listening for visibility and focus changes
   * Call this during app initialization
   */
  public start(): void {
    console.log('[RefreshTokenValidation] Starting refresh token validation service');
    
    this.ngZone.runOutsideAngular(() => {
      // Listen to page visibility changes (user switches tabs or minimizes window)
      fromEvent(document, 'visibilitychange')
        .pipe(
          debounceTime(500), // Debounce rapid visibility changes
          filter(() => !document.hidden) // Only process when page becomes visible
        )
        .subscribe(() => {
          this.ngZone.run(() => {
            this.onPageBecomeVisible('visibilitychange');
          });
        });

      // Listen to window focus changes (user returns to window after system lock/sleep)
      fromEvent(window, 'focus')
        .pipe(
          debounceTime(500) // Debounce rapid focus changes
        )
        .subscribe(() => {
          this.ngZone.run(() => {
            this.onPageBecomeVisible('focus');
          });
        });

      // Listen to document visibility state changes
      merge(
        fromEvent(document, 'resume'), // Mobile app resumed
        fromEvent(window, 'beforeunload'), // Before page unload
      )
        .pipe(debounceTime(500))
        .subscribe(() => {
          this.ngZone.run(() => {
            this.onPageBecomeVisible('resume');
          });
        });
    });
  }

  /**
   * Stop listening for visibility changes
   * Call this on logout
   */
  public stop(): void {
    console.log('[RefreshTokenValidation] Stopping refresh token validation service');
    this.isDestroyed = true;
    this.visibilityCheckSubject$.complete();
  }

  /**
   * Handle page becoming visible again after being hidden
   * Validates refresh token hasn't expired
   */
  private onPageBecomeVisible(source: string): void {
    if (this.isDestroyed) {
      return;
    }

    console.log(`[RefreshTokenValidation] Page visible event triggered from: ${source}`);

    // Check if user is authenticated
    if (!this.authService.canNavigateToDashboard()) {
      console.log('[RefreshTokenValidation] User not authenticated - skipping validation');
      return;
    }
  }
}
